import React, { useEffect, useState, useCallback, useMemo } from "react";
import { format } from "date-fns";
import TooltipHeader from "components/Tooltip/TooltipHeader";
import axiosInstance from "services/httpInterceptor";

const linkStyle = {
  color: "inherit",
  textDecoration: "none",
  cursor: "pointer",
};

const withLink = (value, url) => (
  <a href={url} target="_blank" rel="noopener noreferrer" style={linkStyle}>
    {value}
  </a>
);

const useProductMentionsTableData = (targetCompanyId, presetKey) => {

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const staticColumns = useMemo(() => [
    {
      key: "date",
      name: "Date",
      align: "center",
      noWrap: true,
      width: "100px",
    },
    {
      key: "productName",
      name: "Product Name",
      align: "left",
      width: "150px",
      header: () => (
        <TooltipHeader
          label="Product Name"
          tooltip="The specific product that is mentioned in the content. How to use: Quickly identify which product each piece of content refers to for targeted analysis."
          align="left"
        />
      ),
      render: (v, row) => withLink(v, row.url),
    },
    {
      key: "productPost",
      name: "Product Post",
      align: "left",
      width: "320px",
      header: () => (
        <TooltipHeader
          label="Product Post"
          tooltip="A summary of the content where the product is mentioned. How to use: Read the relevant excerpt to understand the context of the product mention without needing to open the source immediately."
          align="center"
        />
      ),
      render: (v, row) => withLink(v, row.url),
    },
    {
      key: "contentCategory",
      name: "Content Category",
      align: "center",
      width: "140px",
      header: () => (
        <TooltipHeader
          label="Content Category"
          tooltip="The infered purpose of the content where the product is mentioned. How to use: Understand the goals of your competitors and strategy behind a specific post and use it to improve your own content strategy."
          align="center"
        />
      ),
    },
    {
      key: "engagement",
      name: "Engagement",
      align: "center",
      width: "100px",
      header: () => (
        <TooltipHeader
          label="Engagement"
          tooltip="A score or metric indicating user interaction with the content where the product was mentioned (if available from the source). How to use: Gauge the relative impact or reach of different product mentions. Higher engagement can indicate greater visibility or resonance."
          align="center"
        />
      ),
      render: (v) => v != null ? `${v.toFixed(2)} %` : "",
    },
    {
      key: "resonance",
      name: "Resonance",
      align: "center",
      width: "100px",
      header: () => (
        <TooltipHeader
          label="Resonance"
          tooltip="A score or metric indicating how deeply the content's message connected with its audience, often emphasizing meaningful interactions. How to use: Identify mentions that sparked significant discussion or connection, indicating highly impactful content."
          align="center"
        />
      ),
      render: (v) => v != null ? `${v.toFixed(2)} %` : "",
    },
    { key: "likes", name: "Likes", align: "center", width: "80px" },
    { key: "comments", name: "Comments", align: "center", width: "80px" },
    { key: "shares", name: "Shares", align: "center", width: "80px" },
  ], []);

  const fetchData = useCallback(async () => {
    if (!targetCompanyId || !presetKey) {
      setRows([]);
      setTotalPages(0);
      setTotalElements(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = {
        companyId: targetCompanyId,
        preset: presetKey.toUpperCase(),
        page: currentPage,
        size: pageSize,
      };
      const response = await axiosInstance.get("/v1/products/mentions", { params });
      const data = response.data;

      setTotalPages(data.totalPages || 0);
      setTotalElements(data.totalElements || 0);

      const mappedRows = (data.content || []).map((mention) => ({
        url: mention.url,
        date: mention.timestamp ? format(new Date(mention.timestamp), "dd-MM-yyyy") : "",
        productName: mention.productMention?.canonicalName,
        productPost: mention.summary || "No snippet available.",
        contentCategory: mention.contentType,
        engagement: mention.engagement ?? null,
        resonance: mention.resonance ?? null,
        likes: mention.nlikes,
        comments: mention.ncomments,
        shares: mention.nshares,
      }));
      setRows(mappedRows);

    } catch (err) {
      setError("Please try again.");
      setRows([]);
      setTotalPages(0);
      setTotalElements(0);
    }
    setLoading(false);
  }, [targetCompanyId, presetKey, currentPage, pageSize]);

  useEffect(() => {
    if (currentPage !== 0) {
      setCurrentPage(0);
    }
  }, [targetCompanyId, presetKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
  };

  return {
    columns: staticColumns,
    rows,
    loading,
    error,
    currentPage,
    totalPages,
    totalElements,
    pageSize,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
  };
};

export default useProductMentionsTableData;
